import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card, Statistic, Row, Col, Spin, Button, Tag, Table,
  message, Empty
} from 'antd';
import {
  ArrowLeftOutlined, ReloadOutlined, MailOutlined, EyeOutlined,
  AimOutlined, CheckCircleOutlined, WarningOutlined, FormOutlined
} from '@ant-design/icons';
import moment from 'moment';
import EmailCampaignService from '../../../services/EmailCampaignService';
import ROUTES from '../../../constants/routes';
import { PageContainer, PageHeader } from '../../Surveys/styled';

const CampaignAnalyticsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const result = await EmailCampaignService.getAnalytics(id);
    if (result.success) {
      setData(result.data);
    } else {
      message.error(result.error);
    }
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <PageContainer>
        <div style={{ padding: 60, textAlign: 'center' }}><Spin size="large" /></div>
      </PageContainer>
    );
  }

  if (!data) {
    return <PageContainer><Empty description="No analytics available" /></PageContainer>;
  }

  const { campaign, stats, rates, recentEvents } = data;

  return (
    <PageContainer>
      <PageHeader>
        <div>
          <Button
            icon={<ArrowLeftOutlined />}
            type="text"
            onClick={() => navigate(ROUTES.ADMIN_EMAIL_CAMPAIGNS)}
            style={{ marginBottom: 8 }}
          >
            Back to Campaigns
          </Button>
        </div>
        <Button icon={<ReloadOutlined />} onClick={load}>Refresh</Button>
      </PageHeader>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic title="Recipients" value={stats.totalRecipients} prefix={<MailOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="Delivered"
              value={stats.delivered}
              suffix={`(${rates.deliveryRate}%)`}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="Opened"
              value={stats.opened}
              suffix={`(${rates.openRate}%)`}
              prefix={<EyeOutlined />}
              valueStyle={{ color: '#1677ff' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="Clicked"
              value={stats.clicked}
              suffix={`(${rates.clickRate}%)`}
              prefix={<AimOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic title="Sent" value={stats.sent} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="Bounced"
              value={stats.bounced}
              suffix={`(${rates.bounceRate}%)`}
              prefix={<WarningOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic title="Failed" value={stats.failed} valueStyle={{ color: '#ff4d4f' }} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic title="Unsubscribed" value={stats.unsubscribed} />
          </Card>
        </Col>
      </Row>

      {campaign.linkedSurveyId && (
        <Card title="Survey Conversions" style={{ marginBottom: 24 }}>
          <Row gutter={16}>
            <Col xs={12} sm={8}>
              <Statistic
                title="Survey Responses"
                value={stats.surveyResponseCount}
                prefix={<FormOutlined />}
                valueStyle={{ color: '#E31B23' }}
              />
            </Col>
            <Col xs={12} sm={8}>
              <Statistic
                title="Conversion Rate"
                value={rates.conversionRate}
                suffix="%"
              />
            </Col>
          </Row>
        </Card>
      )}

      <Card title="Recent Activity">
        {recentEvents?.length ? (
          <Table
            size="small"
            dataSource={recentEvents}
            rowKey="_id"
            pagination={false}
            columns={[
              {
                title: 'Time',
                dataIndex: 'timestamp',
                width: 180,
                render: (d) => moment(d).format('MMM D, h:mm:ss A')
              },
              {
                title: 'Recipient',
                dataIndex: 'recipientId',
                render: (r) => r?.email || '—'
              },
              {
                title: 'Event',
                dataIndex: 'event',
                width: 120,
                render: (e) => <Tag>{e}</Tag>
              },
              {
                title: 'Details',
                key: 'details',
                render: (_, e) => e.url || e.reason || '—'
              }
            ]}
          />
        ) : (
          <Empty description="No events yet" />
        )}
      </Card>
    </PageContainer>
  );
};

export default CampaignAnalyticsPage;
